/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import PropTypes from 'prop-types'
import {KTCard} from '../Card/KTCard'
import {KTCardBody} from '../Card/KTCardBody'
import {KTCardHeader} from '../Card/KTCardHeader'
import Dropdown from 'react-bootstrap/Dropdown'
import {KTSVG, toAbsoluteUrl} from '../../helpers'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {
  fileFolderGetId,
  fileFolderGetAllOnlyFolder,
  fileFolderCreate,
  fileCreate,
  fileDelete,
  fileFolderDelete,
  fileFolderRename,
  fileRename,
  fileDownloadAsZip,
  fileFolderUpdate,
  fileFolderGetAll,
  assignFileFolderPermission,
  fileFolderPermission,
} from '../../redux/slices/common.slice'
import {FileTypeIcon} from './fileIcons'
import { projectUsersLookup } from '../../redux/slices/lookup.slice'
import {DeleteModal, Select} from '..'
import {ListLoading} from '../Table/ListLoading'
import {Modal} from 'react-bootstrap'
import debounce from 'lodash/debounce'

const FolderTreeView = ({folders, onFolderClick, activeFolderId}) => {
  const folderMap = {}
  const initialExpandedState = {}

  folders.forEach((folder) => {
    if (!folderMap[folder.rootFileFolderId]) {
      folderMap[folder.rootFileFolderId] = []
    }
    folderMap[folder.rootFileFolderId].push(folder)

    if (folder.rootFileFolderId === 0) {
      initialExpandedState[folder.id] = true
    }
  })

  const [expandedFolders, setExpandedFolders] = useState(initialExpandedState)

  const toggleFolder = (folderId, event) => {
    event.stopPropagation()
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }))
  }

  const renderFolderTree = (parentId = 0, level = 0) => {
    if (!folderMap[parentId]) return null

    return (
      <ul
        className={`folder-tree-list ${level === 0 ? 'ps-0' : 'ps-7'} mb-0`}
        style={{listStyleType: 'none'}}
      >
        {folderMap[parentId].map((folder) => {
          const hasChildren = folderMap[folder.id] && folderMap[folder.id].length > 0
          const isExpanded = expandedFolders[folder.id]

          return (
            <li key={folder.id} className='mb-2'>
              <div
                className={`d-flex align-items-center cursor-pointer py-1 px-2 rounded ${
                  activeFolderId === folder.id ? 'bg-light-primary' : 'bg-hover-light-primary'
                }`}
                onClick={() => onFolderClick(folder.id, folder.rootFileFolderId)}
              >
                {hasChildren && (
                  <span
                    className='svg-icon svg-icon-primary me-2 cursor-pointer'
                    onClick={(e) => toggleFolder(folder.id, e)}
                  >
                    {isExpanded ? (
                      <svg
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <rect
                          opacity='0.3'
                          x='2'
                          y='2'
                          width='20'
                          height='20'
                          rx='5'
                          fill='currentColor'
                        />
                        <rect
                          x='6.0104'
                          y='10.9247'
                          width='12'
                          height='2'
                          rx='1'
                          fill='currentColor'
                        />
                      </svg>
                    ) : (
                      <svg
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <rect
                          opacity='0.3'
                          x='2'
                          y='2'
                          width='20'
                          height='20'
                          rx='5'
                          fill='currentColor'
                        />
                        <rect
                          x='10.8891'
                          y='17.8033'
                          width='12'
                          height='2'
                          rx='1'
                          transform='rotate(-90 10.8891 17.8033)'
                          fill='currentColor'
                        />
                        <rect
                          x='6.01041'
                          y='10.9247'
                          width='12'
                          height='2'
                          rx='1'
                          fill='currentColor'
                        />
                      </svg>
                    )}
                  </span>
                )}

                {!hasChildren && <span className='svg-icon svg-icon-primary me-2 ms-4'></span>}

                <span className={`svg-icon svg-icon-primary me-2${folder.isSecure ? ' position-relative' : ''}`}>
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      opacity='0.3'
                      d='M10 4H21C21.6 4 22 4.4 22 5V7H10V4Z'
                      fill='currentColor'
                    />
                    <path
                      d='M9.2 3H3C2.4 3 2 3.4 2 4V19C2 19.6 2.4 20 3 20H21C21.6 20 22 19.6 22 19V7C22 6.4 21.6 6 21 6H12L10.4 3.60001C10.2 3.20001 9.7 3 9.2 3Z'
                      fill='currentColor'
                    />
                  </svg>
                  {folder.isSecure && (
                    <span className='position-absolute' style={{bottom: -2, right: -4, fontSize: '10px'}}>
                      <i className='fa fa-lock text-danger' style={{fontSize: '10px'}} />
                    </span>
                  )}
                </span>

                <span
                  className={`fw-bold text-nowrap ${
                    activeFolderId === folder.id ? 'text-primary' : 'text-gray-800'
                  }`}
                >
                  {folder.name}
                </span>
              </div>

              {hasChildren && isExpanded && renderFolderTree(folder.id, level + 1)}
            </li>
          )
        })}
      </ul>
    )
  }

  return <div className='folder-tree'>{renderFolderTree()}</div>
}

const FolderSelectionTreeView = ({folders, onFolderSelect, selectedFolderId}) => {
  const folderMap = {}

  folders.forEach((folder) => {
    if (!folderMap[folder.rootFileFolderId]) {
      folderMap[folder.rootFileFolderId] = []
    }
    folderMap[folder.rootFileFolderId].push(folder)
  })

  const renderFolderTree = (parentId = 0, level = 0) => {
    if (!folderMap[parentId]) return null

    return (
      <ul
        className={`folder-tree-list ${level === 0 ? 'ps-0' : 'ps-9'} mb-0`}
        style={{listStyleType: 'none'}}
      >
        {folderMap[parentId].map((folder) => {
          const hasChildren = folderMap[folder.id] && folderMap[folder.id].length > 0

          return (
            <li key={folder.id} className='mb-2'>
              <div className='d-flex align-items-center py-1 px-2 rounded bg-hover-light-primary mb-2'>
                <div className='form-check form-check-custom form-check-solid me-2'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='folderSelection'
                    value={folder.id}
                    id={`folder-${folder.id}`}
                    checked={selectedFolderId === folder.id}
                    onChange={() => onFolderSelect(folder.id)}
                  />
                </div>

                <span className='svg-icon svg-icon-primary me-2'>
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      opacity='0.3'
                      d='M10 4H21C21.6 4 22 4.4 22 5V7H10V4Z'
                      fill='currentColor'
                    />
                    <path
                      d='M9.2 3H3C2.4 3 2 3.4 2 4V19C2 19.6 2.4 20 3 20H21C21.6 20 22 19.6 22 19V7C22 6.4 21.6 6 21 6H12L10.4 3.60001C10.2 3.20001 9.7 3 9.2 3Z'
                      fill='currentColor'
                    />
                  </svg>
                </span>

                <label
                  htmlFor={`folder-${folder.id}`}
                  className='form-check-label fw-bold cursor-pointer text-gray-800 text-nowrap fs-6'
                >
                  {folder.name}
                </label>
              </div>

              {hasChildren && renderFolderTree(folder.id, level + 1)}
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <div className='folder-selection-tree' style={{overflowY: 'auto'}}>
      {renderFolderTree()}
    </div>
  )
}

const FileManager = ({collapse, SourceId, SourceTypeId}) => {
  const intl = useIntl()
  const [deleteFileId, setDeleteFileId] = useState()
  const [deleteFolderId, setDeleteFolderId] = useState()
  const [showFileModal, setShowFileModal] = useState(false)
  const [showFolderModal, setShowFolderModal] = useState(false)
  const [rootFileFolderId, setRootFileFolderId] = useState([])
  const [rootFileFolderNowId, setRootFileFolderNowId] = useState()
  const [newFolder, setNewFolder] = useState(false)
  const [newFile, setNewFile] = useState(false)
  const [reNameInput, setReNameInput] = useState()
  const [reNameInputShow, setReNameInputShow] = useState(null) // { index, type: 'folder' | 'file' }
  const [moveFileId, setMoveFileId] = useState()
  const [showMoveFileModal, setShowMoveFileModal] = useState(false)
  const [rootFileFolderNewId, setRootFileFolderNewId] = useState()
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [showAssignFileFolderPermissionModal, setShowAssignFileFolderPermissionModal] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState()
  const [adminUser, setAdminUser] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = React.useRef(0)
  const [selectedFileIds, setSelectedFileIds] = useState([])
  const [isDownloading, setIsDownloading] = useState(false)

  const {
    getFileFolderId,
    getAllWithFiles,
    file,
    fileFolder,
    isDeleted,
    isRedirect,
    error,
    loading,
    getAllOnlyFolder,
    updateFolder,
    fileFolderPermission: fileFolderPermissionData,
  } = useSelector((state) => state.common)
  const {projectUsersLookupList} = useSelector((state) => state.lookup)
  const {info: userInfo} = useSelector((state) => state.profile)
  const dispatch = useDispatch()

  useEffect(() => {
    if (userInfo) {
      setAdminUser(userInfo.roles.includes('Admin'))
    }
  }, [userInfo])

  useEffect(() => {
    dispatch(fileFolderGetId({SourceId, SourceTypeId}))
    dispatch(fileFolderGetAllOnlyFolder({SourceId, SourceTypeId}))
  }, [SourceId, SourceTypeId, updateFolder])

  useEffect(() => {
    if (getFileFolderId) {
      setRootFileFolderNowId(getFileFolderId && getFileFolderId.rootFileFolderId)
      setRootFileFolderId([getFileFolderId && getFileFolderId.rootFileFolderId])
    }
  }, [getFileFolderId])

  useEffect(() => {
    if (rootFileFolderNowId && !loading)
      dispatch(
        fileFolderGetAll({
          SourceId,
          SourceTypeId,
          RootFileFolderId: rootFileFolderNowId,
          Name: search,
        })
      )
  }, [file, fileFolder, isDeleted, isRedirect, rootFileFolderNowId])

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      setSearch(searchValue.toUpperCase())
    }, 1000),
    []
  )

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedSearch(value)
  }

  useEffect(() => {
    if (search && !loading) {
      const rootFolderId = getFileFolderId ? getFileFolderId.rootFileFolderId : rootFileFolderNowId

      setRootFileFolderNowId(rootFolderId)
      setRootFileFolderId([rootFolderId])

      dispatch(
        fileFolderGetAll({
          SourceId,
          SourceTypeId,
          RootFileFolderId: rootFolderId,
          Name: search,
        })
      )
    } else if (!search && rootFileFolderNowId && !loading) {
      dispatch(
        fileFolderGetAll({
          SourceId,
          SourceTypeId,
          RootFileFolderId: rootFileFolderNowId,
          Name: search,
        })
      )
    }
  }, [search])

  const validSchema = Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  })

  const [formValues] = useState({
    SourceId,
    SourceTypeId,
    RootFileFolderId: '',
    Name: intl.formatMessage({id: 'FILE.NEWFOLDER'}),
    FormFile: null,
  })

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      if (newFolder)
        dispatch(
          fileFolderCreate({
            SourceId,
            SourceTypeId,
            RootFileFolderId: rootFileFolderNowId,
            Name: values.Name,
          })
        )
      if (newFile)
        dispatch(
          fileCreate({
            SourceId: rootFileFolderNowId,
            SourceTypeId: 19,
            FormFile: values.FormFile,
          })
        )
      if (newFile && !values.FormFile) return false
      setSubmitting(true)
      setNewFolder(false)
      setNewFile(false)
      resetForm()
    },
  })

  const handleClickDeleteFile = (id) => {
    setDeleteFileId(id)
    setShowFileModal(true)
  }

  const handleDeleteFile = () => {
    dispatch(fileDelete(deleteFileId))
    setShowFileModal(false)
  }

  const handleClickDeleteFolder = (id) => {
    setDeleteFolderId(id)
    setShowFolderModal(true)
  }

  const handleDeleteFolder = () => {
    dispatch(fileFolderDelete({Id: deleteFolderId, SourceId, SourceTypeId}))
    setShowFolderModal(false)
  }

  const onClickFolder = (id, rootId) => {
    setRootFileFolderId([...rootFileFolderId, rootId])
    setRootFileFolderNowId(id)
    setNewFolder(false)
    setNewFile(false)
    setSelectedFileIds([])
    dispatch(fileFolderGetAll({SourceId, SourceTypeId, RootFileFolderId: id, Name: search}))
  }

  const removeValue = (value) => {
    setRootFileFolderId((prevState) => {
      const newState = [...prevState]

      const index = newState.lastIndexOf(value)
      if (index !== -1) {
        newState.splice(index, 1)
      }

      return newState
    })
  }

  const onClickUp = (rootId) => {
    removeValue(rootId)
    setRootFileFolderNowId(rootId)
    setNewFolder(false)
    setNewFile(false)
    setSelectedFileIds([])
    dispatch(fileFolderGetAll({SourceId, SourceTypeId, RootFileFolderId: rootId, Name: search}))
  }

  const onSubmitReName = () => {
    if (reNameInput && reNameInput.id && reNameInput.value) {
      if (reNameInputShow && reNameInputShow.type === 'file') {
        dispatch(fileRename({Id: reNameInput.id, Name: reNameInput.value}))
      } else {
        dispatch(
          fileFolderRename({
            SourceId,
            SourceTypeId,
            Id: reNameInput.id,
            Name: reNameInput.value,
          })
        )
      }
    }
    setReNameInputShow(null)
    setNewFolder(false)
    setNewFile(false)
  }

  const onClickReset = () => {
    setNewFolder(false)
    setNewFile(false)
    formik.resetForm()
  }

  const handleClickMoveFile = (id) => {
    setMoveFileId(id)
    setShowMoveFileModal(true)
  }

  const handleMoveFile = () => {
    if (rootFileFolderNewId) {
      dispatch(
        fileFolderUpdate({
          Id: moveFileId,
          RootFileFolderId: rootFileFolderNowId,
          NewRootFileFolderId: rootFileFolderNewId,
        })
      )
      setShowMoveFileModal(false)
      setRootFileFolderNewId(undefined)
    }
  }

  const parseFileSize = (sizeString) => {
    if (!sizeString || typeof sizeString !== 'string') return 0

    const sizeStr = sizeString.toLowerCase().trim()
    const match = sizeStr.match(/^([\d,.-]+)\s*(kb|mb|gb|tb|b)?/)

    if (!match) return 0

    const numericValue = parseFloat(match[1].replace(/,/g, ''))
    const unit = match[2] || 'b'

    const multipliers = {
      b: 1,
      kb: 1024,
      mb: 1024 * 1024,
      gb: 1024 * 1024 * 1024,
      tb: 1024 * 1024 * 1024 * 1024,
    }

    return numericValue * (multipliers[unit] || 1)
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortedData = (data) => {
    if (!sortField || !data) return data

    const sortedData = [...data].sort((a, b) => {
      let aValue, bValue

      switch (sortField) {
        case 'name':
          aValue = (a.name && a.name.toLowerCase()) || ''
          bValue = (b.name && b.name.toLowerCase()) || ''
          break
        case 'size':
          if (a.sourceTypeId === 19) {
            aValue = parseFileSize(a.contentLengthName)
          } else {
            aValue = 0
          }

          if (b.sourceTypeId === 19) {
            bValue = parseFileSize(b.contentLengthName)
          } else {
            bValue = 0
          }
          break
        case 'lastModified':
          aValue = new Date(a.createdDate || 0)
          bValue = new Date(b.createdDate || 0)
          break
        default:
          return 0
      }

      if (sortField === 'lastModified') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
      } else {
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
        return 0
      }
    })

    return sortedData
  }

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <KTSVG
          path='/images/icons/duotune/arrows/arr089.svg'
          className='svg-icon-5 ms-1 text-muted'
        />
      )
    }

    return sortDirection === 'asc' ? (
      <KTSVG
        path='/images/icons/duotune/arrows/arr073.svg'
        className='svg-icon-5 ms-1 text-primary'
      />
    ) : (
      <KTSVG
        path='/images/icons/duotune/arrows/arr072.svg'
        className='svg-icon-5 ms-1 text-primary'
      />
    )
  }

  const handleClickAssignFileFolderPermission = (id) => {
    setShowAssignFileFolderPermissionModal(id)
    dispatch(projectUsersLookup(SourceId))
    dispatch(fileFolderPermission({
      FileFolderId: id,
      SourceId: SourceId,
      SourceTypeId: SourceTypeId
    }))
  }

  const handleAssignFileFolderPermission = (id) => {
    dispatch(assignFileFolderPermission({
      FileFolderId: id,
      SourceId: SourceId,
      SourceTypeId: SourceTypeId,
      UserIds: selectedUserId
    }))
    setShowAssignFileFolderPermissionModal(false)
    setSelectedUserId(undefined)
  }
  


  const handleBulkDownload = async () => {
    setIsDownloading(true)
    try {
      await dispatch(fileDownloadAsZip({fileIds: selectedFileIds}))
      setSelectedFileIds([])
    } finally {
      setIsDownloading(false)
    }
  }

  useEffect(() => {
    if (fileFolderPermissionData && fileFolderPermissionData.userIds) {
      setSelectedUserId(fileFolderPermissionData.userIds.map((user) => user.id))
    }
  }, [fileFolderPermissionData])

  return (
    <>
      <KTCard className='mb-5 mt-5'>
        <KTCardHeader title={intl.formatMessage({id: 'FILE.TITLE'})} collapsed={collapse}>
          <div className='card-toolbar'>
            <div className='d-flex justify-content-end'>
              <div className='d-flex align-items-center position-relative me-3'>
                <KTSVG
                  path='/images/icons/duotune/general/gen021.svg'
                  className='svg-icon-1 position-absolute ms-3'
                />
                <input
                  type='text'
                  className='form-control form-control-solid  w-200px ps-14'
                  placeholder={intl.formatMessage({id: 'FILE.SEARCH'})}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              {rootFileFolderId && rootFileFolderId.length > 1 && (
                <button
                  className='btn btn-icon btn-light-primary me-3'
                  onClick={() => onClickUp(rootFileFolderId[rootFileFolderId.length - 1])}
                >
                  <i className='fa fa-level-up' aria-hidden='true'></i>
                </button>
              )}

              <button
                type='button'
                className='btn btn-flex btn-light-primary me-3'
                onClick={() => {
                  setNewFolder(!newFolder)
                  setNewFile(false)
                }}
              >
                <KTSVG path='/images/icons/duotune/files/fil013.svg' className='svg-icon-2' />
                {intl.formatMessage({id: 'FILE.NEWFOLDER'})}
              </button>

              <button
                type='button'
                className='btn btn-flex btn-primary me-3'
                onClick={() => {
                  setNewFile(!newFile)
                  setNewFolder(false)
                }}
              >
                <i className='fa fa-cloud-upload' aria-hidden='true'></i>{' '}
                {intl.formatMessage({id: 'FILE.NEWFILE'})}
              </button>

              {selectedFileIds.length > 0 && (
                <button
                  className='btn btn-flex btn-light-success'
                  disabled={isDownloading}
                  onClick={handleBulkDownload}
                >
                  <i className='fa fa-download me-2' />
                  {isDownloading
                    ? intl.formatMessage({id: 'FILE.DOWNLOAD.PREPARING'})
                    : intl.formatMessage({id: 'FILE.DOWNLOAD.SELECTED'}, {count: selectedFileIds.length})}
                </button>
              )}
            </div>
          </div>
        </KTCardHeader>

        <KTCardBody className='p-5' collapseble isShow={collapse}>
          <div className='row'>
            <div className='table-responsive pe-5 border-end col-md-3'>
              {getAllOnlyFolder && getAllOnlyFolder.length > 0 ? (
                <FolderTreeView
                  folders={getAllOnlyFolder}
                  onFolderClick={onClickFolder}
                  activeFolderId={rootFileFolderNowId}
                />
              ) : (
                <div className='text-center py-5'>
                  <span className='text-muted'>{intl.formatMessage({id: 'FILE.NOFOLDERS'})}</span>
                </div>
              )}
            </div>
            <div
              className='table-responsive ps-5 col-md-9 min-h-400px position-relative'
              onDragEnter={(e) => {
                e.preventDefault()
                dragCounter.current++
                setIsDragging(true)
              }}
              onDragOver={(e) => {
                e.preventDefault()
              }}
              onDragLeave={() => {
                dragCounter.current--
                if (dragCounter.current === 0) setIsDragging(false)
              }}
              onDrop={(e) => {
                e.preventDefault()
                dragCounter.current = 0
                setIsDragging(false)
                const files = e.dataTransfer.files
                if (files.length > 0) {
                  const acceptedExts = [
                    'image/', '.pdf', '.doc', '.docx', '.kmz', '.kml', '.ncz', '.dwg',
                    '.zip', '.rar', '.mp4', '.xls', '.xlsx', 'video/',
                  ]
                  const validFiles = Array.from(files).filter((f) =>
                    acceptedExts.some((ext) =>
                      f.type.startsWith(ext) || f.name.toLowerCase().endsWith(ext)
                    )
                  )
                  if (validFiles.length > 0) {
                    const dt = new DataTransfer()
                    validFiles.forEach((f) => dt.items.add(f))
                    dispatch(
                      fileCreate({SourceId: rootFileFolderNowId, SourceTypeId: 19, FormFile: dt.files})
                    )
                  }
                }
              }}
            >
              {isDragging && (
                <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light-primary opacity-75 rounded border border-primary border-dashed' style={{zIndex: 10}}>
                  <span className='fs-3 fw-bold text-primary'>{intl.formatMessage({id: 'FILE.DRAGANDDROP'})}</span>
                </div>
              )}
              {search && (
                <div className='alert alert-primary d-flex align-items-center p-3 mb-3'>
                  <div className='d-flex flex-column'>
                    <span className='fs-6 fw-bold'>
                      {search} {intl.formatMessage({id: 'FILE.SEARCH.FOR'})}
                    </span>
                    <span className='fs-7'>
                      {getAllWithFiles && getAllWithFiles.length > 0
                        ? intl.formatMessage(
                            {id: 'FILE.SEARCH.FOUND'},
                            {count: getAllWithFiles.length}
                          )
                        : intl.formatMessage({id: 'FILE.SEARCH.NOTFOUND'})}
                    </span>
                  </div>
                </div>
              )}
              <table
                id='kt_file_manager_list'
                data-kt-filemanager-table='folders'
                className='table align-middle table-row-dashed fs-6 gy-5'
              >
                <thead>
                  <tr className='text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0'>
                    <th className='w-30px'>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        checked={
                          selectedFileIds.length > 0 &&
                          selectedFileIds.length ===
                            ((getAllWithFiles ? getAllWithFiles.filter((x) => x.sourceTypeId === 19) : []).length)
                        }
                        onChange={(e) => {
                          const fileItems = getAllWithFiles ? getAllWithFiles.filter((x) => x.sourceTypeId === 19) : []
                          setSelectedFileIds(e.target.checked ? fileItems.map((x) => x.id) : [])
                        }}
                      />
                    </th>
                    <th className='min-w-200px'>
                      <div
                        className='d-flex align-items-center cursor-pointer user-select-none'
                        onClick={() => handleSort('name')}
                      >
                        <span>{intl.formatMessage({id: 'FILE.NAME'})}</span>
                        {getSortIcon('name')}
                      </div>
                    </th>
                    <th className='min-w-10px'>
                      <div
                        className='d-flex align-items-center cursor-pointer user-select-none'
                        onClick={() => handleSort('size')}
                      >
                        <span>{intl.formatMessage({id: 'FILE.SIZE'})}</span>
                        {getSortIcon('size')}
                      </div>
                    </th>
                    <th className='min-w-125px'>
                      <div
                        className='d-flex align-items-center cursor-pointer user-select-none'
                        onClick={() => handleSort('lastModified')}
                      >
                        <span>{intl.formatMessage({id: 'FILE.LASTMODIFIEDINFO'})}</span>
                        {getSortIcon('lastModified')}
                      </div>
                    </th>
                    <th>&nbsp;</th>
                  </tr>
                </thead>

                <tbody className='fw-semibold text-gray-600'>
                  {loading && (
                    <tr>
                      <td colSpan='5' className='p-5'>
                        <ListLoading />
                      </td>
                    </tr>
                  )}

                  {(newFolder || newFile) && (
                    <tr>
                      <td></td>
                      <td
                        colSpan='4'
                        className='fv-row fv-plugins-bootstrap5 fv-plugins-framework fv-plugins-icon-container'
                      >
                        <form className='form' onSubmit={formik.handleSubmit} noValidate>
                          <div className='d-flex'>
                            {newFolder && (
                              <>
                                <span className='svg-icon svg-icon-2x svg-icon-primary me-4 mt-3'>
                                  <svg
                                    width='24'
                                    height='24'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                  >
                                    <path
                                      opacity='0.3'
                                      d='M10 4H21C21.6 4 22 4.4 22 5V7H10V4Z'
                                      fill='currentColor'
                                    />
                                    <path
                                      d='M9.2 3H3C2.4 3 2 3.4 2 4V19C2 19.6 2.4 20 3 20H21C21.6 20 22 19.6 22 19V7C22 6.4 21.6 6 21 6H12L10.4 3.60001C10.2 3.20001 9.7 3 9.2 3Z'
                                      fill='currentColor'
                                    />
                                  </svg>
                                </span>
                                <div className='me-3'>
                                  <input
                                    className='form-control'
                                    type='text'
                                    required
                                    autoComplete='off'
                                    placeholder={intl.formatMessage({id: 'FILE.NEWFOLDER.INPUT'})}
                                    disabled={formik.isSubmitting}
                                    onFocus={(event) => event.target.select()}
                                    {...formik.getFieldProps('Name')}
                                  />
                                  {formik.touched.Name && formik.errors.Name && (
                                    <div className='fv-plugins-message-container'>
                                      <div className='fv-help-block'>
                                        <span role='alert'>{formik.errors.Name}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}

                            {newFile && (
                              <div className='mw-250px me-3'>
                                <label
                                  htmlFor='imageFile'
                                  className='dropzone dz-clickable d-flex w-100'
                                >
                                  <input
                                    id='imageFile'
                                    className='position-absolute z-index-2 opacity-0'
                                    style={{width: 0, height: 0}}
                                    type='file'
                                    accept='image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.kmz,.kml,.ncz,.dwg,.zip,.rar,application/zip,application/x-rar-compressed,application/x-zip-compressed,application/x-7z-compressed,.kmz,.kml,.ncz,.dwg,.mp4,.xls,.xlsx,video/*,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                                    name={'FormFile'}
                                    multiple
                                    onChange={(event) =>
                                      formik.setFieldValue('FormFile', event.currentTarget.files)
                                    }
                                  />
                                  <div className='dz-message needsclick'>
                                    <KTSVG
                                      path='/images/icons/duotune/communication/com008.svg'
                                      className='svg-icon-1 position-relative z-index-1'
                                    />
                                    <div className='ms-4'>
                                      <h3 className='fs-5 fw-bold text-gray-900 mb-1'>
                                        {intl.formatMessage({id: 'BUTTON.IMAGE'})}
                                      </h3>
                                    </div>
                                  </div>
                                </label>
                              </div>
                            )}

                            <button type='submit' className='btn btn-icon btn-light-primary me-3'>
                              <i className='fa fa-check' aria-hidden='true'></i>
                            </button>

                            <div
                              className='btn btn-icon btn-light-danger'
                              onClick={() => onClickReset()}
                            >
                              <i className='fa fa-close' aria-hidden='true'></i>
                            </div>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}

                  {newFile &&
                    formik.values.FormFile &&
                    Array.from(formik.values.FormFile).map((file, index) => (
                      <tr key={index}>
                        <td></td>
                        <td>
                          <div className='d-flex align-items-center'>
                            <KTSVG
                              path='/images/icons/duotune/files/fil005.svg'
                              className='svg-icon-1 position-relative z-index-1 me-4 svg-icon-success'
                            />
                            <span className='text-gray-800'>{file.name}</span>
                          </div>
                        </td>
                        <td>{file.size / 1024} KB</td>
                        <td>
                          {intl.formatDate(new Date(), {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                          })}
                        </td>
                        <td></td>
                      </tr>
                    ))}

                  {getSortedData(getAllWithFiles) &&
                    getSortedData(getAllWithFiles).map((item, index) => {
                      if (item && item.sourceTypeId == 19) {
                        return (
                          <tr key={index}>
                            <td>
                              <div className='form-check form-check-custom form-check-solid'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  checked={selectedFileIds.includes(item.id)}
                                  onChange={(e) => {
                                    setSelectedFileIds((prev) =>
                                      e.target.checked
                                        ? [...prev, item.id]
                                        : prev.filter((id) => id !== item.id)
                                    )
                                  }}
                                />
                              </div>
                            </td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <FileTypeIcon name={item.name} contentType={item.contentType} />
                                {reNameInputShow && reNameInputShow.index === index && reNameInputShow.type === 'file' ? (
                                  <div className='d-flex'>
                                    <div className='mw-250px me-3'>
                                      <input
                                        className='form-control'
                                        type='text'
                                        required
                                        autoComplete='off'
                                        placeholder={intl.formatMessage({id: 'FILE.NEWFOLDER.INPUT'})}
                                        onFocus={(event) => event.target.select()}
                                        defaultValue={item.name}
                                        onChange={(event) =>
                                          setReNameInput({id: item.id, value: event.target.value})
                                        }
                                      />
                                    </div>
                                    <button
                                      className='btn btn-icon btn-light-primary me-3'
                                      onClick={onSubmitReName}
                                    >
                                      <i className='fa fa-check' aria-hidden='true'></i>
                                    </button>
                                    <button
                                      className='btn btn-icon btn-light-danger'
                                      onClick={() => setReNameInputShow(null)}
                                    >
                                      <i className='fa fa-close' aria-hidden='true'></i>
                                    </button>
                                  </div>
                                ) : (
                                  <a
                                    href={item.fileUrl}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='text-gray-800 text-hover-primary'
                                  >
                                    {item.name}
                                  </a>
                                )}
                              </div>
                            </td>
                            <td>{item.contentLengthName}</td>
                            <td>
                              {intl.formatDate(new Date(item.createdDate), {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric',
                              })}{' '}
                              - {item.lastModifiedBy}
                            </td>
                            <td className='text-end'>
                              <div className='d-flex justify-content-end align-items-center'>
                                <button
                                  type='button'
                                  className='btn btn-icon btn-sm btn-light-primary me-1'
                                  title={intl.formatMessage({id: 'FILE.PREVIEW'})}
                                  onClick={() =>
                                    setPreviewFile({
                                      name: item.name,
                                      fileUrl: item.fileUrl,
                                      contentType: item.contentType,
                                    })
                                  }
                                >
                                  <i className='fa fa-eye' />
                                </button>
                                <a
                                  href={item.fileUrl}
                                  target='_blank'
                                  rel='noreferrer'
                                  className='btn btn-icon btn-sm btn-light-success me-1'
                                  title={intl.formatMessage({id: 'BUTTON.DOWNLOAD'})}
                                >
                                  <i className='fa fa-download' />
                                </a>
                                <div className='ms-1'>
                                  <Dropdown autoClose='outside' align={{lg: 'end'}}>
                                    <Dropdown.Toggle
                                      variant='light'
                                      size='sm'
                                      className='iconless btn-sm btn-icon btn-light btn-active-light-primary'
                                    >
                                      <KTSVG
                                        path='/images/icons/duotune/general/gen053.svg'
                                        className='svg-icon-2'
                                      />
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className='menu menu-sub menu-sub-dropdown menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold py-3'>
                                      <Dropdown.Item
                                        onClick={() =>
                                          setReNameInputShow({index, type: 'file'})
                                        }
                                        className='menu-item px-3'
                                      >
                                        <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                          {intl.formatMessage({id: 'FILE.RENAME'})}
                                        </div>
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => handleClickMoveFile(item.id)}
                                        className='menu-item px-3'
                                      >
                                        <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                          {intl.formatMessage({id: 'BUTTON.MOVE'})}
                                        </div>
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => handleClickDeleteFile(item.id)}
                                        className='menu-item px-3'
                                      >
                                        <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                          {intl.formatMessage({id: 'BUTTON.DELETE'})}
                                        </div>
                                      </Dropdown.Item>
                                    </Dropdown.Menu>
                                  </Dropdown>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )
                      }

                      return (
                        <tr key={index}>
                          <td></td>
                          <td>
                            <div className='d-flex align-items-center'>
                              <span className={`svg-icon svg-icon-2x svg-icon-primary me-4${item.isSecure ? ' position-relative' : ''}`}>
                                <svg
                                  width='24'
                                  height='24'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  xmlns='http://www.w3.org/2000/svg'
                                >
                                  <path
                                    opacity='0.3'
                                    d='M10 4H21C21.6 4 22 4.4 22 5V7H10V4Z'
                                    fill='currentColor'
                                  />
                                  <path
                                    d='M9.2 3H3C2.4 3 2 3.4 2 4V19C2 19.6 2.4 20 3 20H21C21.6 20 22 19.6 22 19V7C22 6.4 21.6 6 21 6H12L10.4 3.60001C10.2 3.20001 9.7 3 9.2 3Z'
                                    fill='currentColor'
                                  />
                                </svg>
                                {item.isSecure && (
                                  <span className='position-absolute' style={{bottom: 0, right: -2, fontSize: '12px'}}>
                                    <i className='fa fa-lock text-danger' style={{fontSize: '12px'}} />
                                  </span>
                                )}
                              </span>

                              {!(reNameInputShow && reNameInputShow.index === index && reNameInputShow.type === 'folder') && (
                                <div
                                  className='text-gray-800 text-hover-primary'
                                  role='button'
                                  onClick={() => onClickFolder(item.id, item.rootFileFolderId)}
                                >
                                  {item.name}
                                </div>
                              )}

                              {reNameInputShow && reNameInputShow.index === index && reNameInputShow.type === 'folder' && (
                                <div className='d-flex'>
                                  <div className='mw-250px me-3'>
                                    <input
                                      className='form-control'
                                      type='text'
                                      required
                                      autoComplete='off'
                                      placeholder={intl.formatMessage({id: 'FILE.NEWFOLDER.INPUT'})}
                                      disabled={formik.isSubmitting}
                                      onFocus={(event) => event.target.select()}
                                      defaultValue={item.name}
                                      onChange={(event) =>
                                        setReNameInput({id: item.id, value: event.target.value})
                                      }
                                    />
                                  </div>

                                  <button
                                    className='btn btn-icon btn-light-primary me-3'
                                    onClick={onSubmitReName}
                                  >
                                    <i className='fa fa-check' aria-hidden='true'></i>
                                  </button>

                                  <button
                                    className='btn btn-icon btn-light-danger'
                                    onClick={() => setReNameInputShow(null)}
                                  >
                                    <i className='fa fa-close' aria-hidden='true'></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>-</td>
                          <td>
                            {intl.formatDate(new Date(item.createdDate), {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: 'numeric',
                            })}{' '}
                            - {item.lastModifiedBy}
                          </td>
                          <td className='text-end'>
                            <div className='d-flex justify-content-end'>
                              <div className='ms-2'>
                                <Dropdown autoClose='outside' align={{lg: 'end'}}>
                                  <Dropdown.Toggle
                                    variant='light'
                                    size='sm'
                                    className='iconless btn-sm btn-icon btn-light btn-active-light-primary'
                                  >
                                    <KTSVG
                                      path='/images/icons/duotune/general/gen053.svg'
                                      className='svg-icon-2'
                                    />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu className='menu menu-sub menu-sub-dropdown menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold py-3'>
                                    <Dropdown.Item
                                      className='menu-item px-3'
                                      onClick={() => setReNameInputShow({index, type: 'folder'})}
                                    >
                                      <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                        {intl.formatMessage({id: 'FILE.RENAME'})}
                                      </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handleClickDeleteFolder(item.id)}
                                      className='menu-item px-3'
                                    >
                                      <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                        {intl.formatMessage({id: 'BUTTON.DELETE'})}
                                      </div>
                                    </Dropdown.Item>
                                    {adminUser && (<Dropdown.Item
                                        onClick={() => handleClickAssignFileFolderPermission(item.id)}
                                        className='menu-item px-3'
                                      >
                                        <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                          {intl.formatMessage({id: 'BUTTON.ASSIGN_FILE_FOLDER_PERMISSION'})}
                                        </div>
                                      </Dropdown.Item>
                                    )}
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )
                    })}

                  {getAllWithFiles && getAllWithFiles.length < 1 && !loading && (
                    <tr className='odd'>
                      <td valign='top' colSpan='6' className='dataTables_empty'>
                        <div className='d-flex flex-column flex-center'>
                          <img
                            src={toAbsoluteUrl('/images/empty.png')}
                            alt='empty'
                            className='mw-400px'
                          />
                          <div className='fs-1 fw-bolder text-dark mb-4'>
                            {intl.formatMessage({id: 'FILE.EMPTYFOLDER'})}
                          </div>
                          <div className='fs-6'>
                            {intl.formatMessage({id: 'FILE.EMPTYFOLDER.TEXT'})}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </KTCardBody>
      </KTCard>

      <Modal show={showMoveFileModal} onHide={() => setShowMoveFileModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'BUTTON.MOVE'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='mb-3'>
            <label className='form-label fw-bold mb-3'>
              {intl.formatMessage({id: 'FILE.SELECT_DESTINATION_FOLDER'})}
            </label>
            {getAllOnlyFolder && getAllOnlyFolder.length > 0 ? (
              <FolderSelectionTreeView
                folders={getAllOnlyFolder}
                onFolderSelect={setRootFileFolderNewId}
                selectedFolderId={rootFileFolderNewId}
              />
            ) : (
              <div className='alert alert-warning'>
                {intl.formatMessage({id: 'FILE.NOFOLDERS'})}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex align-items-center justify-content-center w-100'>
            <div className='d-flex align-items-center me-3 gap-3'>
              <button
                type='button'
                className='btn btn-light'
                onClick={() => setShowMoveFileModal(false)}
              >
                {intl.formatMessage({id: 'BUTTON.CANCEL'})}
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleMoveFile}
                disabled={!rootFileFolderNewId}
              >
                {intl.formatMessage({id: 'BUTTON.MOVE'})}
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showAssignFileFolderPermissionModal} onHide={() => setShowAssignFileFolderPermissionModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'BUTTON.ASSIGN_FILE_FOLDER_PERMISSION'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='mb-3'>
            <label className='form-label fw-bold mb-3'>
              {intl.formatMessage({id: 'FILE.SELECT_USERS'})}
            </label>
            <Select
              data={projectUsersLookupList}
              required
              isMulti
              menuPortalTarget={null}
              closeMenuOnSelect={false}
              onChange={(item) => {
                const newData = []
                item && item.map((data) => newData.push(data.value))
                setSelectedUserId(newData)
              }}
              value={selectedUserId}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex align-items-center justify-content-center w-100'>
            <div className='d-flex align-items-center me-3 gap-3'>
              <button
                type='button'
                className='btn btn-light'
                onClick={() => setShowAssignFileFolderPermissionModal(false)}
              >
                {intl.formatMessage({id: 'BUTTON.CANCEL'})}
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={() => handleAssignFileFolderPermission(showAssignFileFolderPermissionModal)}
                disabled={!selectedUserId}
              >
                {intl.formatMessage({id: 'BUTTON.ASSIGN_FILE_FOLDER_PERMISSION'})}
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <DeleteModal
        onClick={handleDeleteFile}
        onOpen={showFileModal}
        onClose={() => setShowFileModal(false)}
        error={error}
      />

      <DeleteModal
        onClick={handleDeleteFolder}
        onOpen={showFolderModal}
        onClose={() => setShowFolderModal(false)}
        error={error}
      />

      <Modal
        show={!!previewFile}
        onHide={() => setPreviewFile(null)}
        centered
        size='xl'
      >
        <Modal.Header closeButton>
          <Modal.Title>{previewFile && previewFile.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{minHeight: '60vh'}}>
          {previewFile && (() => {
            const ct = previewFile.contentType || ''
            const nameParts = previewFile.name ? previewFile.name.split('.') : []
            const ext = nameParts.length > 1 ? nameParts.pop().toLowerCase() : ''
            const url = previewFile.fileUrl

            if (ct.startsWith('image/') || ['jpg','jpeg','png','gif','bmp','webp','svg'].includes(ext)) {
              return <img src={url} alt={previewFile.name} className='img-fluid w-100' />
            }
            if (ct === 'application/pdf' || ext === 'pdf') {
              return <iframe src={url} title={previewFile.name} className='w-100 h-100' style={{minHeight: '60vh', border: 'none'}} />
            }
            if (ct.startsWith('video/') || ['mp4', 'mov', 'avi'].includes(ext)) {
              return <video controls src={url} className='w-100' />
            }
            if (['doc', 'docx', 'xls', 'xlsx'].includes(ext)) {
              const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
              return <iframe src={officeUrl} title={previewFile.name} className='w-100 h-100' style={{minHeight: '60vh', border: 'none'}} />
            }
            return (
              <div className='d-flex flex-column align-items-center justify-content-center h-100 py-10'>
                <i className='fa fa-file-o text-muted' style={{fontSize: '64px'}} />
                <p className='text-muted mt-4'>{intl.formatMessage({id: 'FILE.PREVIEW.UNSUPPORTED'})}</p>
                <a href={url} target='_blank' rel='noreferrer' className='btn btn-primary mt-2'>
                  <i className='fa fa-download me-2' />
                  {intl.formatMessage({id: 'BUTTON.DOWNLOAD'})}
                </a>
              </div>
            )
          })()}
        </Modal.Body>
      </Modal>
    </>
  )
}

FileManager.propTypes = {
  collapse: PropTypes.bool,
  SourceId: PropTypes.number,
  SourceTypeId: PropTypes.number,
}

FileManager.defaultProps = {
  collapse: true,
}

export {FileManager}
